import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Background,
  type BackgroundKind,
  DEFAULT_PATTERN,
  GRADIENT_PRESETS,
} from '@/entities/appearance';
import { Button, ColorSwatches } from '@/shared/ui';

import styles from './editor.module.css';
import { FocalPoint } from './FocalPoint';
import { GradientEditor } from './GradientEditor';
import { PatternEditor } from './PatternEditor';
import type { BackgroundImageDraft } from './use-background-image-draft';

const NONE: Background = {
  v: 1,
  kind: 'none',
  color: null,
  gradient: null,
  pattern: null,
  position_x: 0.5,
  position_y: 0.5,
  overlay: 0,
};

const KINDS: readonly BackgroundKind[] = ['none', 'color', 'gradient', 'pattern', 'image'];

interface Props {
  value: Background | null;
  onChange: (value: Background | null) => void;
  image: BackgroundImageDraft;
  /** Offered kinds (a column only has "none" and "image": its colors are look styles). */
  kinds?: readonly BackgroundKind[];
  idPrefix: string;
}

/**
 * Background of the opened board / a column: none, one color, gradient, pattern or an image.
 * An image gets a focal point and an overlay so cards stay readable.
 */
export function BackgroundEditor({ value, onChange, image, kinds = KINDS, idPrefix }: Props) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const background = value ?? NONE;

  const select = (kind: BackgroundKind) => {
    const preset = GRADIENT_PRESETS[0];
    switch (kind) {
      case 'none':
        onChange(null);
        return;
      case 'color':
        onChange({ ...NONE, kind, color: background.color ?? '#0079bf' });
        return;
      case 'gradient':
        onChange({
          ...NONE,
          kind,
          gradient: background.gradient ?? (preset ? { ...preset } : null),
        });
        return;
      case 'pattern':
        onChange({ ...NONE, kind, pattern: background.pattern ?? DEFAULT_PATTERN });
        return;
      case 'image':
        onChange({
          ...NONE,
          kind,
          position_x: background.position_x,
          position_y: background.position_y,
          overlay: background.overlay > 0 ? background.overlay : 0.25,
        });
        return;
    }
  };

  return (
    <div className={styles.section}>
      <div
        className={styles.options}
        role="radiogroup"
        aria-label={t('appearance.background.kind')}
      >
        {kinds.map((kind) => (
          <button
            key={kind}
            type="button"
            role="radio"
            aria-checked={background.kind === kind}
            className={styles.option}
            data-testid={`${idPrefix}-bg-${kind}`}
            onClick={() => {
              select(kind);
            }}
          >
            <strong>{t(`appearance.background.kinds.${kind}`)}</strong>
          </button>
        ))}
      </div>

      {background.kind === 'color' && (
        <ColorSwatches
          label={t('appearance.color')}
          value={background.color}
          onChange={(color) => {
            if (color) onChange({ ...background, color });
          }}
        />
      )}
      {background.kind === 'gradient' && background.gradient && (
        <GradientEditor
          value={background.gradient}
          onChange={(gradient) => {
            onChange({ ...background, gradient });
          }}
        />
      )}
      {background.kind === 'pattern' && background.pattern && (
        <PatternEditor
          value={background.pattern}
          onChange={(pattern) => {
            onChange({ ...background, pattern });
          }}
        />
      )}
      {background.kind === 'image' && (
        <div className={styles.section}>
          <div className={styles.row}>
            <Button
              onClick={() => {
                input.current?.click();
              }}
            >
              {t(
                image.previewUrl
                  ? 'appearance.background.replaceImage'
                  : 'appearance.background.chooseImage',
              )}
            </Button>
            {image.previewUrl && (
              <Button variant="ghost" onClick={image.remove}>
                {t('appearance.background.removeImage')}
              </Button>
            )}
            <input
              ref={input}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="visually-hidden"
              aria-label={t('appearance.background.chooseImage')}
              data-testid={`${idPrefix}-bg-file`}
              onChange={(event) => {
                image.setFile(event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
            />
          </div>
          <p className={styles.hint}>{t('appearance.background.imageHint')}</p>
          {image.previewUrl ? (
            <>
              <FocalPoint
                src={image.previewUrl}
                x={background.position_x}
                y={background.position_y}
                onChange={(x, y) => {
                  onChange({ ...background, position_x: x, position_y: y });
                }}
              />
              <label className={styles.slider}>
                <span>
                  {t('appearance.background.overlay', {
                    percent: Math.round(background.overlay * 100),
                  })}
                </span>
                <input
                  type="range"
                  min={0}
                  max={80}
                  step={5}
                  value={Math.round(background.overlay * 100)}
                  onChange={(event) => {
                    onChange({ ...background, overlay: Number(event.target.value) / 100 });
                  }}
                />
              </label>
            </>
          ) : (
            <p className={styles.hint}>{t('appearance.background.noImage')}</p>
          )}
        </div>
      )}
    </div>
  );
}
